import { useEffect, useRef, useState } from "react";
import { VideoOff } from "lucide-react";

function extractId(url = "") {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return url.length === 11 ? url : "";
}

let apiPromise = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
  return apiPromise;
}

export default function YouTubePlayer({ url, completed, onProgress, onEnded }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const videoId = extractId(url);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadYT().then(() => {
      if (!mounted || !containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              clearInterval(intervalRef.current);
              intervalRef.current = setInterval(() => {
                try {
                  const t = playerRef.current.getCurrentTime();
                  onProgress && onProgress(t, false);
                } catch {}
              }, 5000);
            } else {
              clearInterval(intervalRef.current);
            }
            if (e.data === window.YT.PlayerState.ENDED) {
              try {
                onProgress && onProgress(playerRef.current.getDuration(), true);
              } catch {}
              onEnded && onEnded();
            }
          },
        },
      });
    });
    return () => {
      mounted = false;
      clearInterval(intervalRef.current);
      try { playerRef.current?.destroy?.(); } catch {}
    };
  }, [videoId]); // eslint-disable-line

  if (!videoId) {
    return (
      <div className="flex flex-col items-center justify-center text-center gap-2 p-8 border border-dashed border-[#E5E7EB] rounded-lg bg-white" data-testid="video-empty">
        <VideoOff className="w-7 h-7 text-[#9CA3AF]" />
        <p className="text-sm font-semibold text-[#1c1b1b]">No video for this module</p>
        <p className="text-xs text-[#434654]">You can continue to the assessment.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-[#E5E7EB] bg-black" data-testid="youtube-player">
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <div ref={containerRef} className="absolute inset-0 w-full h-full" />
      </div>
      {!ready && <div className="text-xs text-white/70 p-2">Loading video…</div>}
    </div>
  );
}
